import {useCallback} from 'react';
import {useQuery} from '@tanstack/react-query';

import {useStores} from '../../stores/use-stores';

import {derivePromotions} from './derivePromotions';
import {EMPTY_DERIVED_PROMOTIONS} from './emptyDerivedPromotions';
import {promotionsEnabled, promotionsQueryOptions} from './promotionsQueryOptions';
import {PromotionArms} from './types';

/**
 * MUST be called from an `observer()` component. The members-only gate reads
 * MobX (`userStore.isLoggedIn`, `shopSelectionStore.loyaltyEnabled`) at derive
 * time; without `observer()` the collections will not re-derive on login or
 * store change.
 */
export const usePromotions = () => {
  const {shopSelectionStore, userStore} = useStores();
  const selectedStore = shopSelectionStore.selectedStore;
  const storeNumber = selectedStore?.store_number;
  const storeState = selectedStore?.address?.administrative_area;

  // Read as primitives at the top of render. That read is what registers the
  // MobX dependency for `observer()`, and it is what lets `select` below be
  // memoised on two booleans instead of on a fresh object literal.
  const loyaltyEnabled = !!shopSelectionStore.loyaltyEnabled;
  const isLoggedIn = !!userStore.isLoggedIn;

  // Memoised so `select` keeps a stable identity across renders. TanStack reuses
  // the previous derived result only when `options.select === this.#selectFn`
  // AND the raw data is unchanged; a fresh closure every render fails that
  // check, so `derivePromotions` re-ran on every render and the returned arrays
  // stayed referentially stable only because structural sharing happened to
  // deep-compare them back to the previous objects. `DepartmentScreen`'s focus
  // effect keys on `rawPromotions`, so that identity is load-bearing: with
  // structural sharing disabled and an unmemoised `select`, a probe measured 50
  // identity changes and 51 `navigate` calls across 52 renders.
  const select = useCallback(
    (arms: PromotionArms) => derivePromotions(arms, {loyaltyEnabled, isLoggedIn}),
    [loyaltyEnabled, isLoggedIn],
  );

  const query = useQuery({
    ...promotionsQueryOptions(storeNumber, storeState),
    select,
  });

  return {
    ...(query.data ?? EMPTY_DERIVED_PROMOTIONS),
    // Terminal on EVERY outcome, including the disabled ones. `GiftCardsPromotionWaiter`
    // gates its fallback on this and spins forever otherwise — with `enabled: false`,
    // `isFetched` never becomes true. This MUST use the same predicate as `enabled`:
    // guarding only `enabled` for the gift card store would leave this permanently
    // false there and hang that waiter.
    hasFetchedPromotions: !promotionsEnabled(storeNumber) || query.isFetched,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};
